'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CheckboxInput } from '@app/app/_components/Input/CheckboxInput';
import { Message } from '@app/app/_components/Message/Message';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useIsMobile } from '@app/infrastructure/application/useIsMobile';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useFormState } from 'react-dom';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { EsalesTransportFooter } from '@app/app/esales/transport/components/EsalesTransportFooter';
import {
    requestTravelInsurance,
    TravelInsuranceRequestResult,
} from '@app/service/eshop/travel/actions/requestTravelInsurance';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { ProductDataTerritories } from '@app/domain/model/eshop/transport/ProductDataTerritories';
import { FormState } from '@app/lib/form/contracts/FormState';
import { DatePicker } from '@app/app/_components/DatePicker/DatePicker';
import { addDays, startOfDay } from 'date-fns';
import { useState, useMemo } from 'react';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { useHighContrastMode } from '@app/app/_context/AccessibilityContext/useHighContrastMode';
import { Select } from '@app/app/_components/Input/Select/Select';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';

interface Props {
    onSuccess?: (result: TravelInsuranceRequestResult) => void;
    backgroundImageSrc: string;
    mobileImageSrc: string;
    stakeholderLogoSrc: string;
    vigLogoSrc: string;
    purposes: ProductDataPurpose;
    territories: ProductDataTerritories;
    initialRequestData: TravelInsuranceRequestResult;
}

export function TravelLandingSection({
    onSuccess,
    backgroundImageSrc,
    mobileImageSrc,
    stakeholderLogoSrc,
    vigLogoSrc,
    purposes,
    territories,
    initialRequestData,
}: Props) {
    const t = useTranslations();
    const isMobile = useIsMobile(1024);
    const locale = useLocale();
    const parameters = useAppParameters();
    const isHighContrastMode = useHighContrastMode();

    const [beginDate, setBeginDate] = useState<Nullable<Date>>(
        initialRequestData.beginDate ? new Date(initialRequestData.beginDate) : null,
    );

    const today = startOfDay(new Date());
    const maxBeginDate = addDays(today, 90);
    const minEndDate = beginDate ? startOfDay(beginDate) : today;
    const maxEndDate = beginDate ? addDays(startOfDay(beginDate), 60) : addDays(today, 60);

    const defaultBeginDate = useMemo(
        () => (initialRequestData.beginDate ? new Date(initialRequestData.beginDate) : undefined),
        [initialRequestData.beginDate],
    );

    const defaultEndDate = useMemo(
        () => (initialRequestData.endDate ? new Date(initialRequestData.endDate) : undefined),
        [initialRequestData.endDate],
    );

    const purposesOptions = useMemo(
        () =>
            purposes.purposes.map(purpose => ({
                value: purpose.code,
                label: purpose.name,
            })),
        [purposes],
    );

    const territoriesOptions = useMemo(
        () =>
            territories.territories.map(territory => ({
                value: territory.code,
                label: territory.name,
            })),
        [territories],
    );

    const [formState, formAction] = useFormState(
        (prevState: FormState<TravelInsuranceRequestResult>, formData: FormData) =>
            requestTravelInsurance(prevState, formData, purposes, territories),
        { status: FormStatus.Pending },
    );

    useChangeEffect(() => {
        if (formState.status === FormStatus.Success && formState.data != null) {
            onSuccess?.(formState.data);
        }
    }, [formState]);

    return (
        <div className="flex min-h-screen flex-col">
            <div className="relative flex flex-col bg-white py-12 lg:px-16">
                {!isMobile && !isHighContrastMode && (
                    <div className="absolute right-0 top-0 h-full lg:w-1/2 xl:w-[60%] 2xl:w-full">
                        <Image
                            src={backgroundImageSrc}
                            draggable={false}
                            fill
                            className="object-cover object-center"
                            alt={t('eshop.transport.backgroundImageAlt')}
                            unoptimized
                        />
                    </div>
                )}
                <form action={formAction} className="relative z-10 px-4 lg:max-w-[520px]">
                    <Heading className="mb-8 text-4xl 2xl:text-nowrap">{t('eshop.travel.landingTitle')}</Heading>

                    <div className="mb-10 flex flex-col gap-6">
                        {formState.status === FormStatus.Error && formState.violations == null && (
                            <Message>{t(formState.message)}</Message>
                        )}

                        <div className="flex w-full flex-col gap-6">
                            <div className="mb-4 flex flex-row justify-between gap-6 max-sm:flex-col">
                                <DatePicker
                                    name="beginDate"
                                    className="flex-grow"
                                    label={t('eshop.travel.beginDateLabel')}
                                    placeholder={t('eshop.travel.dateSelectionPlaceholder')}
                                    minDate={today}
                                    maxDate={maxBeginDate}
                                    onChange={setBeginDate}
                                    error={formState.violations?.beginDate}
                                    defaultValue={defaultBeginDate}
                                />
                                <DatePicker
                                    name="endDate"
                                    className="flex-grow"
                                    label={t('eshop.travel.endDateLabel')}
                                    placeholder={t('eshop.travel.dateSelectionPlaceholder')}
                                    minDate={minEndDate}
                                    maxDate={maxEndDate}
                                    error={formState.violations?.endDate}
                                    defaultValue={defaultEndDate}
                                />
                            </div>
                            <div className="flex flex-col gap-2">
                                <Select
                                    searchable
                                    name="goalOfTheJourneyCode"
                                    label={t('eshop.travel.goalOfTheJourneyLabel')}
                                    options={purposesOptions}
                                    defaultValue={
                                        initialRequestData.goalOfTheJourney.code !== ''
                                            ? initialRequestData.goalOfTheJourney.code
                                            : purposes.defaultPurpose
                                    }
                                />
                                <Select
                                    searchable
                                    name="territoryCode"
                                    label={t('eshop.travel.territoryLabel')}
                                    labelAddon={
                                        <GhostButton
                                            target="_blank"
                                            href={parameters.eshopTravelInfoUrls.territoryVerification}>
                                            <QuestionCircleIcon className="size-4 text-neutral-500" />
                                        </GhostButton>
                                    }
                                    options={territoriesOptions}
                                    defaultValue={
                                        initialRequestData.territory.code !== ''
                                            ? initialRequestData.territory.code
                                            : territories.defaultCode
                                    }
                                />
                            </div>
                        </div>

                        <Message variant="info">
                            {t.rich('eshop.travel.ageExceedsMessage', {
                                link: chunks => (
                                    <TextButton
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopTravelInfoUrls.age75InfoLt
                                                : parameters.eshopTravelInfoUrls.age75InfoEn
                                        }
                                        preventDefault
                                        target="_blank"
                                        className="w-max p-0 underline underline-offset-2">
                                        {chunks}
                                    </TextButton>
                                ),
                            })}
                        </Message>

                        <CheckboxInput
                            name="acceptPrivacyPolicy"
                            label={t.rich('eshop.transport.acceptPrivacyPolicyLabel', {
                                link: chunks => (
                                    <Link
                                        className="underline"
                                        target="_blank"
                                        href={
                                            locale === 'lt'
                                                ? parameters.eshopInfoUrls.privacyPolicy
                                                : parameters.eshopInfoUrls.privacyPolicyEn
                                        }>
                                        {chunks}
                                    </Link>
                                ),
                            })}
                            defaultChecked={initialRequestData.beginDate !== ''}
                            error={formState.violations?.acceptPrivacyPolicy}
                        />
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        <Button rightAddon={<ArrowRightIcon className="size-4" />}>{t('general.next')}</Button>
                        <Button
                            href={parameters.eshopTravelInfoUrls.readMoreInfo}
                            target="_blank"
                            rightAddon={<ArrowRightIcon className="size-4" />}
                            variant="outlined">
                            {t('eshop.general.readMore')}
                        </Button>
                    </div>
                </form>
            </div>
            <div className="relative h-[600px] w-full lg:hidden">
                <Image
                    src={mobileImageSrc}
                    fill
                    className="object-cover"
                    alt={t('eshop.transport.backgroundImageAlt')}
                    unoptimized
                />
            </div>

            <EsalesTransportFooter
                className="lg:mt-[500px]"
                stakeholderLogoSrc={stakeholderLogoSrc}
                vigLogoSrc={vigLogoSrc}
            />
        </div>
    );
}
